import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { StatusBar, useColorScheme } from 'react-native'
import asyncStorage from '../shims/asyncStorage'

import type { ReactNode } from 'react'

import { ThemeContext } from './util'
import type {
  Preference,
  ThemeDefinition,
  ThemeContextType,
} from './util/types'

import techappDark from './techapp-dark'
import techappLight from './techapp-light'
import useResponsiveScreen from './util/useResponsiveScreen'

export {
  isMobile,
  useComponentStyle,
  useTheme,
  useThemedStylesheet,
} from './util'
export type {
  ColorType,
  ComponentStyleSheet,
  ComponentThemeFactory,
  ComponentThemeFromDefinition,
  ComponentThemeProps,
  ConsumerRTHSeries,
  ConsumerRTHState,
  FontType,
  GenericStylesheet,
  Preference,
  Theme,
  ThemeContextType,
  ThemeDefinition,
  ThemeFactory,
} from './util/types'

export function ThemeContextProvider({
  darkTheme = techappDark,
  lightTheme = techappLight,
  defaultPreference = 'system',
  ignoreStoredPreference = false,
  children,
}: {
  darkTheme?: ThemeDefinition
  lightTheme?: ThemeDefinition
  defaultPreference?: 'light' | 'dark' | 'system'
  ignoreStoredPreference?: boolean
  children: ReactNode
}) {
  const [gotPreference, setGotPreference] = useState(false)
  const [preference, setPreference] = useState<Preference>(defaultPreference)

  const systemMode = useColorScheme()
  const activeMode =
    preference === 'system' ? (systemMode ?? 'light') : preference
  const activeTheme = activeMode === 'dark' ? darkTheme : lightTheme
  const { screenSize, isLandscape, isPortrait } = useResponsiveScreen({
    breakpoints: activeTheme.breakpoints,
  })

  const expandedTheme: ThemeContextType['theme'] = useMemo(
    () => ({
      ...activeTheme,
      activeMode,
      barStyle: activeMode === 'dark' ? 'light-content' : 'dark-content',
    }),
    [activeTheme, activeMode],
  )

  const savePreference = useCallback(async (pref: Preference) => {
    setPreference(pref)
    await asyncStorage.setItem('themePreference', pref)
  }, [])

  const ctx = useMemo(
    () => ({
      theme: expandedTheme,
      preference,
      screenSize,
      isLandscape,
      isPortrait,
      savePreference,
    }),
    [
      expandedTheme,
      screenSize,
      savePreference,
      preference,
      isLandscape,
      isPortrait,
    ],
  )

  useEffect(() => {
    setPreference(defaultPreference)
  }, [defaultPreference])

  useEffect(() => {
    if (ignoreStoredPreference) {
      setGotPreference(true)

      return
    }
    void (async () => {
      try {
        const storedPreference = await asyncStorage.getItem('themePreference')
        if (
          storedPreference &&
          ['light', 'dark', 'system'].includes(storedPreference)
        ) {
          setPreference(storedPreference as Preference)
        }
        setGotPreference(true)
      } catch {
        setGotPreference(true)
      }
    })()
  }, [ignoreStoredPreference])

  return (
    <>
      <StatusBar
        backgroundColor={activeTheme.colors.uiHeaderBackground}
        barStyle={expandedTheme.barStyle}
      />
      <ThemeContext.Provider value={ctx}>
        {gotPreference && children}
      </ThemeContext.Provider>
    </>
  )
}

export const useThemeContext = () => useContext(ThemeContext)

export function useThemePreference() {
  return useContext(ThemeContext).preference
}

export function useSaveThemePreference() {
  return useContext(ThemeContext).savePreference
}
